import { useState, useEffect, useMemo, useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import Mobile from "./components/Mobile";

import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

import './tickets.css';

const Ticketing = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const location = useLocation();
  const title = (location.state && location.state.title) || undefined;
  const startsAt = (location.state && location.state.startsAt) || undefined;

  const isMobile = useMediaQuery("(max-width:600px)");
  const [ticketTypes, setTicketTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(true);

  // Cart-related state moved to parent so it can be shared by Mobile / Desktop
  const [promo, setPromo] = useState("");
  const [qty, setQty] = useState({}); // { [ticketTypeId]: number }
  const [cartTotals, setCartTotals] = useState({ subtotal: 0, fees: 0, total: 0 });
  const [cartId, setCartId] = useState(null);
  const [cartExpiresAt, setCartExpiresAt] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState(null);
  const [hydrating, setHydrating] = useState(false);

  const skipNextUpsertRef = useRef(false);

  // Load ticket types when eventId changes (route-based)
  useEffect(() => {
    if (!eventId) return;
    getTicketTypes();
    setDialogOpen(true);
    hydrateCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const getTicketTypes = async () => {
    const result = await request(
      `ticketing/tickets/availability/?eventId=${eventId}`
    );
    if (result.success) {
      setTicketTypes(result.data || []);
      // seed quantity map for new ids
      setQty((prev) => {
        const next = { ...prev };
        (result.data || []).forEach((t) => {
          if (!(t.id in next)) next[t.id] = 0;
        });
        // prune removed ids
        Object.keys(next).forEach((k) => {
          if (!result.data.find((t) => t.id === k)) delete next[k];
        });
        return next;
      });
      // Prevent auto-upsert caused by initial qty seeding
      skipNextUpsertRef.current = true;
    } else {
      toast.error(result.error);
    }
  };

  const hydrateCart = async () => {
    try {
      const res = await request(`ticketing/cart/?eventId=${eventId}`);
      if (res?.success && res.data) {
        const c = res.data;
        setCartId(c._id || null);
        setCartExpiresAt(c.expiresAt || null);

        // Convert cents -> dollars if present
        const p = c.pricing || {};
        const subtotal = p.subtotalCents != null ? p.subtotalCents / 100 : (p.subtotal || 0);
        const fees = p.feesCents != null ? p.feesCents / 100 : (p.fees || 0);
        const total = p.totalCents != null ? p.totalCents / 100 : (p.total || 0);
        setCartTotals({ subtotal, fees, total });
        if (c.discountCode) setPromo(c.discountCode);

        // Apply quantities from cart items without triggering an upsert
        skipNextUpsertRef.current = true;
        setHydrating(true);
        setQty((prev) => {
          const next = { ...prev };
          (c.items || []).forEach((it) => {
            if (it.ticketType && Number(it.quantity) > 0) {
              next[String(it.ticketType._id)] = Number(it.quantity) || 0;
            }
          });
          return next;
        });
        setHydrating(false);
      }
    } catch (e) {
      // Silent failure is okay for hydration; user may simply have no active cart
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setTicketTypes([]);
    setQty({});
    setCartTotals({ subtotal: 0, fees: 0, total: 0 });
    setCartId(null);
    setCartExpiresAt(null);
    setCartError(null);
    setPromo("");
    setHydrating(false);
    skipNextUpsertRef.current = false;
    // Prefer going back; fallback to home if no history length
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  // Helpers to enforce max per order / availability
  const getCapFor = (t) => {
    const available = Math.max(0, Number(t.available || 0));
    const mpoNum = Number(t.maxPerOrder);
    const mpo = Number.isFinite(mpoNum) ? mpoNum : Infinity;
    return Math.min(available, mpo);
  };

  const incQty = (ticket) => {
    const cap = getCapFor(ticket);
    setQty((q) => {
      const current = q[ticket.id] || 0;
      const next = Math.min(current + 1, cap);
      return { ...q, [ticket.id]: next };
    });
  };

  const decQty = (ticket) => {
    setQty((q) => ({ ...q, [ticket.id]: Math.max((q[ticket.id] || 0) - 1, 0) }));
  };

  const buildItems = (q) =>
    (ticketTypes || [])
      .map((t) => ({ ticketTypeId: t.id, quantity: Number(q[t.id] || 0) }))
      .filter((it) => it.quantity > 0);

  const postUpsertCart = async ({ items, promoCode }) => {
    let cancelled = false;
    try {
      setCartLoading(true);
      setCartError(null);
      const res = await request('ticketing/cart/upsert', 'post', {
        eventId,
        items,
        discountCode: promoCode || undefined,
      });
      if (cancelled) return;
      if (res && res.success) {
        const d = res.data || {};
        setCartId(d.cart?._id || d.cartId || null);
        setCartExpiresAt(d.cart?.expiresAt || d.expiresAt || null);
        const p = d.pricing || {};
        const subtotal = p.subtotalCents != null ? p.subtotalCents / 100 : (p.subtotal ?? d.subtotal ?? d.cart?.pricing?.subtotal ?? 0);
        const fees = p.feesCents != null ? p.feesCents / 100 : (p.fees ?? d.fees ?? d.cart?.pricing?.fees ?? 0);
        const total = p.totalCents != null ? p.totalCents / 100 : (p.total ?? d.total ?? d.cart?.pricing?.total ?? 0);
        setCartTotals({ subtotal, fees, total });
      } else {
        toast.error(res?.error || 'Failed to update cart');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to update cart');
    } finally {
      setCartLoading(false);
    }
    return () => { cancelled = true; };
  };

  // Apply promo explicitly (only when user clicks Apply and there are items)
  const applyPromo = async () => {
    const items = buildItems(qty);
    if (!items.length) return; // no-op if cart empty
    await postUpsertCart({ items, promoCode: promo });
  };

  // Upsert cart whenever quantities change while dialog is open
  useEffect(() => {
    if (!dialogOpen) return;
    if (!eventId) return;
    if (hydrating) return;
    // Skip the immediate next upsert if set by hydration/seeding
    if (skipNextUpsertRef.current) { skipNextUpsertRef.current = false; return; }

    const items = buildItems(qty);
    if (items.length === 0) {
      // clear totals locally; do not ping server
      setCartTotals({ subtotal: 0, fees: 0, total: 0 });
      setCartId(null);
      setCartExpiresAt(null);
      setCartError(null);
      return;
    }

    postUpsertCart({ items, promoCode: undefined });
  }, [qty, dialogOpen, eventId, hydrating]);

  const totalSelected = useMemo(() => Object.values(qty).reduce((a, b) => a + (b || 0), 0), [qty]);

  const sharedProps = {
    eventId,
    ticketTypes,
    open: dialogOpen,
    close: handleClose,
    title,
    startsAt,

    // quantities & actions
    qty,
    incQty,
    decQty,

    // promo
    promo,
    setPromo,

    // cart state
    cartTotals,
    cartId,
    cartExpiresAt,
    cartLoading,
    cartError,

    // convenience flags
    totalSelected,

    applyPromo,
  };


  return <Mobile {...sharedProps} />;
};

export default Ticketing;
